import type { ComponentProps } from "react";
import { useState } from "react";
import { Popover } from "radix-ui";
import type { DayPicker } from "react-day-picker";
import type { VariantProps } from "tailwind-variants";
import { tv } from "tailwind-variants";

import { formatDate } from "@/shared/lib";
import { selectStyles } from "@/shared/ui/form/select";
import { Calendar } from "../common";

const datePickerVariants = tv({
  slots: {
    trigger: selectStyles().root(),
    content: selectStyles().content({
      className: "max-h-138 max-w-138 rounded-2xl border-none bg-transparent",
    }),
  },
  variants: {
    readOnly: {
      true: {
        trigger:
          "text-neutrals-dark-500 pointer-events-none border-transparent px-0 font-medium shadow-none focus:border-transparent focus:ring-0",
      },
    },
  },
});

type DatePickerProps = VariantProps<typeof datePickerVariants> &
  Pick<ComponentProps<typeof DayPicker>, "startMonth" | "endMonth"> & {
    value?: Date;
    onChange: (date?: Date) => void;
    disabled?: boolean;
    disabledDays?: ComponentProps<typeof DayPicker>["disabled"];
    className?: string;
    placeholder?: string;
  };

export const DatePicker = ({
  value,
  onChange,
  readOnly,
  disabled,
  disabledDays,
  className,
  placeholder = "--/--/----",
  ...rest
}: DatePickerProps) => {
  const { trigger, content } = datePickerVariants({ readOnly, className });

  const [month, setMonth] = useState(value);

  const handleDateChange = (newDate: Date | undefined) => {
    if (!newDate) {
      return;
    }

    if (value) {
      // Combine the new date with the time from the current selected date
      const updatedDate = new Date(
        newDate.getFullYear(),
        newDate.getMonth(),
        newDate.getDate(),
        value.getHours(),
        value.getMinutes(),
        value.getSeconds(),
      );
      onChange(updatedDate);
      setMonth(updatedDate);
    } else {
      onChange(newDate);
    }
  };

  return (
    <Popover.Root>
      <Popover.Trigger
        onClick={() => setMonth(value)}
        className={trigger({ className })}
        disabled={disabled}
      >
        {value ? (
          formatDate(value)
        ) : (
          <span className="text-neutral-700">{placeholder}</span>
        )}
      </Popover.Trigger>

      <Popover.Content sideOffset={8} className={content()} align="center">
        <Calendar
          {...rest}
          disabled={disabledDays}
          month={month}
          onMonthChange={setMonth}
          captionLayout="dropdown"
          mode="single"
          selected={value}
          onSelect={handleDateChange}
          required
          className="size-full"
        />
      </Popover.Content>
    </Popover.Root>
  );
};
